import type { JSX } from 'react';
import { PauseCircleOutlined } from '@ant-design/icons';

import { Tag, type TagColor } from '../ds';
import type { MyPlanCardDto } from '../../lib/api/openingTasks';
import { CARD_STATUS, fmtDMY2, placeLabel, stageTagText } from './plan-model';

/** Тег, который не вылезает за экран телефона: длинный текст обрезается многоточием. */
function PlanTag({ color, children }: { color?: TagColor; children: string }): JSX.Element {
  return (
    <Tag color={color} title={children} style={{ maxWidth: '100%', minWidth: 0 }}>
      <span className="fk-op-ell">{children}</span>
    </Tag>
  );
}

function DateCell({
  label,
  value,
  extra,
  tone,
}: {
  label: string;
  value: string;
  extra?: string;
  tone?: 'muted' | 'ok';
}): JSX.Element {
  return (
    <div className="fk-op-date">
      <div className="fk-op-date-l">{label}</div>
      <div className="fk-op-date-v" data-tone={tone}>
        {value}
      </div>
      {extra ? <div className="fk-op-date-x">{extra}</div> : null}
    </div>
  );
}

/** «≈ 12.10.26» у прогноза, «12.10.26» у зафиксированной даты. */
const dateWithApprox = (d: { date: string; estimated: boolean }): string => `${d.estimated ? '≈ ' : ''}${fmtDMY2(d.date)}`;

/**
 * Шапка карточки открытия (по образцу CRM CardHeader, но только чтение — без кнопок «Аренда подписана», «Пауза»,
 * «Открыто»): ИП, «город, адрес», статус, этап, куратор, счётчики задач и ряд ключевых дат.
 */
export function PlanHeader({ card }: { card: MyPlanCardDto }): JSX.Element {
  const status = CARD_STATUS[card.statusV2];
  const place = placeLabel(card.city, card.address);
  const opened = card.statusV2 === 'opened' || card.statusV2 === 'archived';
  return (
    <section className="fk-op-card fk-op-head" aria-label="Карточка открытия">
      <div style={{ minWidth: 0 }}>
        <h2 className="fk-op-name">{card.partnerName}</h2>
        {place ? <div className="fk-op-place">{place}</div> : null}
        <div className="fk-op-tags">
          <PlanTag color={status.color}>{status.label}</PlanTag>
          <PlanTag>{stageTagText(card.stage)}</PlanTag>
          <PlanTag>{`Куратор: ${card.curatorName ?? 'назначается'}`}</PlanTag>
          {card.overdueTasks > 0 ? <PlanTag color="red">{`просрочено задач: ${card.overdueTasks}`}</PlanTag> : null}
          {card.soonTasks > 0 ? <PlanTag color="orange">{`срок скоро: ${card.soonTasks}`}</PlanTag> : null}
        </div>
      </div>

      {card.statusV2 === 'paused' ? (
        <div className="fk-ot-callout" data-tone="warn" role="status">
          <PauseCircleOutlined />
          <div style={{ minWidth: 0 }}>
            <b>На паузе: сроки и просрочки стоят.</b>
            {card.pauseReason ? ` Причина: ${card.pauseReason}` : ''}
          </div>
        </div>
      ) : null}

      <div className="fk-op-dates">
        <DateCell
          label="Аренда"
          value={card.leaseSignedAt ? fmtDMY2(card.leaseSignedAt) : 'не подписана'}
          extra={card.leaseSignedAt ? 'подписана' : card.lease.date ? `прогноз ${dateWithApprox({ ...card.lease, estimated: true })}` : undefined}
          tone={card.leaseSignedAt ? undefined : 'muted'}
        />
        <DateCell label="Тех. открытие" value={dateWithApprox(card.techOpen)} extra={card.techOpen.estimated ? 'прогноз' : 'план'} />
        <DateCell label="Официальное" value={dateWithApprox(card.officialOpen)} extra={card.officialOpen.estimated ? 'прогноз' : 'план'} />
        {opened && card.openedAt ? (
          <DateCell label="Открыто" value={fmtDMY2(card.openedAt)} extra={card.supportUntil ? `сопровождение до ${fmtDMY2(card.supportUntil)}` : undefined} tone="ok" />
        ) : null}
        <DateCell
          label="Паушальный взнос"
          value={card.feePaidAt ? `оплачен ${fmtDMY2(card.feePaidAt)}` : '—'}
          tone={card.feePaidAt ? 'ok' : 'muted'}
        />
      </div>
    </section>
  );
}
